import { useCallback, useEffect, useRef, useState } from "react";
import { AuthError, type Me } from "../api/client.js";
import { startLive, stopLive } from "../live.js";
import { AnnounceContext } from "./context.js";
import { clearAll, errorMessage, setAuthHandler, setKnownMe, takeMe } from "./data.js";
import { PortalApp } from "./PortalApp.js";
import { SignIn } from "./SignIn.js";

type Phase = { name: "boot" } | { name: "failed"; message: string } | { name: "signin" } | { name: "app"; me: Me };

/** Signed in or not (DESIGN §16.1): the code screen, or the portal around every page. */
export function Shell() {
  const [phase, setPhase] = useState<Phase>({ name: "boot" });
  const [announcement, setAnnouncement] = useState("");
  const announceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const announce = useCallback((text: string) => {
    setAnnouncement("");
    clearTimeout(announceTimer.current);
    announceTimer.current = setTimeout(() => setAnnouncement(text), 50);
  }, []);

  const signedOut = useCallback(() => {
    stopLive();
    clearAll();
    setPhase((p) => (p.name === "signin" ? p : { name: "signin" }));
  }, []);

  const start = useCallback(() => {
    setPhase({ name: "boot" });
    takeMe().then(
      (me) => {
        startLive();
        setPhase({ name: "app", me });
      },
      (err: unknown) => {
        if (err instanceof AuthError) signedOut();
        else setPhase({ name: "failed", message: errorMessage(err) });
      },
    );
  }, [signedOut]);

  useEffect(() => {
    setAuthHandler(signedOut);
    start();
    return stopLive;
  }, [signedOut, start]);

  return (
    <AnnounceContext.Provider value={announce}>
      {phase.name === "boot" ? (
        <div className="boot" role="status">
          Loading…
        </div>
      ) : phase.name === "failed" ? (
        <div className="boot" role="status">
          <p>{phase.message}</p>
          <button type="button" className="btn" onClick={start}>
            Try again
          </button>
        </div>
      ) : phase.name === "signin" ? (
        <SignIn onApproved={start} />
      ) : (
        <PortalApp
          me={phase.me}
          onSignedOut={signedOut}
          onMe={(me) => {
            setKnownMe(me);
            setPhase({ name: "app", me });
          }}
        />
      )}
      <div className="sr" aria-live="polite">
        {announcement}
      </div>
    </AnnounceContext.Provider>
  );
}
